import type { List } from 'immutable';
import type { Moment } from 'moment';

import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { axiosInstance } from '@/api/axiosInstance';
import { duckAppName } from '@/constants';

import type { MockResponseMode } from './enums';
import MockResponseEntity, {
  type MockResponseEntityData,
  type MockResponseEntityRecord,
} from './MockResponse';
import MockSystemRefEntity, {
  type MockSystemRefEntityRecord,
} from './MockSystemRef';

class MockEndpointEntity extends Entity {
  static paths = {
    apiBase: '/mock_api/v1/mock_endpoint/',
  };

  static fields: EntityFields<MockEndpointEntityFields> = {
    uuid: new Fields.IdField(),
    system: new Fields.EntityField({ entity: MockSystemRefEntity }),
    name: new Fields.CharField({ blank: true }),
    description: new Fields.TextField({ blank: true }),
    method: new Fields.CharField({ default: 'GET' }),
    path_pattern: new Fields.CharField(),
    priority: new Fields.IntegerField({ default: 0 }),
    response_mode: new Fields.CharField({ default: 'rules' }),
    is_active: new Fields.BooleanField({ default: true }),
    call_count: new Fields.IntegerField({ blank: true, local: true }),
    path_params: new Fields.AnyField({ blank: true, local: true }),
    url_template: new Fields.CharField({ blank: true, local: true }),
    responses: new Fields.EntityField({
      entity: MockResponseEntity,
      many: true,
      blank: true,
      local: true,
    }),
    can_edit: new Fields.BooleanField({ blank: true, local: true }),
    created: new Fields.DateTimeField({ blank: true, local: true }),
    modified: new Fields.DateTimeField({ blank: true, local: true }),
  };

  static dataToRecord(data: Partial<MockEndpointEntityData>) {
    return super.dataToRecord(data) as MockEndpointEntityRecord;
  }

  static toString(record: MockEndpointEntityRecord | null | undefined) {
    if (!record) return '';
    return `${record.get('method')} ${record.get('path_pattern')}`;
  }

  static resetCounter(uuid: string) {
    return axiosInstance.post<MockEndpointEntityData>(
      `${this.paths.apiBase}${uuid}/reset_counter/`,
    );
  }

  static resolve(payload: MockResolveRequest) {
    return axiosInstance.post<MockResolveResult>(
      `${this.paths.apiBase}resolve/`,
      payload,
    );
  }
}

export type MockEndpointEntityFields = {
  uuid: string;
  system: MockSystemRefEntityRecord;
  name: string;
  description: string;
  method: string;
  path_pattern: string;
  priority: number;
  response_mode: MockResponseMode;
  is_active: boolean;
  call_count: number | null;
  path_params: List<string> | null;
  url_template: string;
  responses: List<MockResponseEntityRecord>;
  can_edit: boolean;
  created: Moment | null;
  modified: Moment | null;
};

export type MockEndpointEntityData = Omit<
  MockEndpointEntityFields,
  'system' | 'path_params' | 'responses' | 'created' | 'modified'
> & {
  system: { uuid: string; name?: string; slug?: string };
  path_params: string[];
  responses: Partial<MockResponseEntityData>[];
  created: string;
  modified: string;
};

export type MockEndpointEntityRecord = EntityRecord<MockEndpointEntityFields>;

export type MockResolveRequest = {
  system: string;
  method: string;
  path: string;
  query?: string;
  headers?: Record<string, string>;
  body?: string;
};

export type MockResolveResult = {
  outcome: 'matched' | 'no_route' | 'bad_method' | 'inactive';
  endpoint: {
    uuid: string;
    method: string;
    path_pattern: string;
    name: string;
  } | null;
  path_params: Record<string, string>;
  response: { uuid: string; name: string; status_code: number } | null;
  selection: string;
  allowed_methods: string[];
};

MockEndpointEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: MockEndpointEntity,
  name: 'MockEndpointEntity',
});

export default MockEndpointEntity;
